/*
  Authentication feedback for the Log In panel on an Event Website (Trello
  #1022).

  The same view serves both logins, but not the same submit. On primary Bobcat
  the login is a whole page, so a wrong password is an ordinary POST that
  re-renders the form with Devise's alert. In the side panel the form is remote,
  Devise answers an XHR failure with 401, and AjaxErrorHandler treats every 401
  the same way it treats an expired session: Turbo.visit(window.location). The
  page reloaded, the drawer went with it, and the participant saw a blink and no
  reason -- their password was gone and nothing said why.

  So the login form's own failure is handled before that global rule sees it.
  The message is rendered into the panel, the address is kept, and focus returns
  to the password so the next attempt is one keystroke away.

  DELIBERATELY PRESENTATIONAL. Nothing here authenticates, decides, or reads a
  response body: the server still refuses exactly as it did, this only reports
  the refusal. That also keeps it out of the way of #1023 -- when login stops
  being a password, this listener stops matching and can be deleted, with no
  Warden failure app or controller override to unpick.
*/
const FORM = '.sidepanel form[action$="/users/sign_in"], .sidepanel form#new_user';
// The exact markup simple_form emits for a field that failed validation: the
// wrapper gains .field_with_errors and holds a <div class="error">. It has to be
// BOTH -- .error alone is unstyled here, and it is the descendant rule
// (.field_with_errors .error) that colours the text and reddens the input
// border. Reproducing the server's own markup means a refused password looks
// like every other invalid field in the product, with no new CSS to keep in
// step. The data attribute is the stable hook for tests, since .error is a
// common class name.
const WRAPPER_ERROR_CLASS = 'field_with_errors';
const ERROR_CLASS = 'error';
const ERROR_HOOK = 'data-login-auth-error';

const LoginPanelErrors = {
  start() {
    document.removeEventListener('ajax:error', this._onError, true);
    // Capture phase, so this runs before AjaxErrorHandler's document-level
    // handler decides to reload the page.
    document.addEventListener('ajax:error', this._onError, true);
  },

  _onError: (event) => {
    const form = event.target.closest && event.target.closest(FORM);
    if (!form) return;

    const [, , xhr] = event.detail || [];
    // Only a refused sign-in. Anything else -- a fault, a real expired session
    // -- keeps the existing behaviour.
    if (!xhr || xhr.status !== 401) return;

    event.stopImmediatePropagation();
    event.preventDefault();
    LoginPanelErrors._show(form);
  },

  _show(form) {
    const password = form.querySelector('input[type="password"], input[name="user[password]"]');
    // The password input itself carries class="input input--full", so the search
    // starts one level up or closest('.input') would match the field, not the
    // simple_form wrapper around it.
    const wrapper = password && password.parentElement
      ? password.parentElement.closest('.input')
      : null;
    const anchor = wrapper || (password ? password.closest('fieldset') : null) || form;
    if (wrapper) wrapper.classList.add(WRAPPER_ERROR_CLASS);

    let message = form.querySelector(`[${ERROR_HOOK}]`);
    if (!message) {
      message = document.createElement('div');
      message.className = ERROR_CLASS;
      message.setAttribute(ERROR_HOOK, 'true');
      message.setAttribute('role', 'alert');
      // The wording is the server's, carried on the form, so the panel says the
      // same thing the full-page login says and says it in the visitor's
      // language.
      message.textContent = form.dataset.authErrorMessage || '';
      anchor.appendChild(message);
    }

    if (!password) return;

    // Keep the address, clear only what was wrong, and put the cursor where the
    // correction goes.
    password.value = '';
    password.setAttribute('aria-invalid', 'true');
    if (password.id) {
      message.id = `${password.id}-error`;
      password.setAttribute('aria-describedby', message.id);
    }
    password.focus();
  }
};

export default LoginPanelErrors;
